import { ReactElement, useEffect, useState } from 'react';
import { FileUploadDeviceCapture } from '@hooks/common';
import { onImageError } from '@helper/utils';
import { RemoveImageAction } from '@components/siteLeads/components/siteProducts/removeImageAction';
import { IUploadPhotosVideosProps } from '.';
import VideoIcon from '@assets/images/video.png';
import { getMobileOperatingSystem } from '@helper/detechUserAgent';

type FileWithPreview = {
	file: File;
	url: string;
};

const isIOS = getMobileOperatingSystem() === 'iOS';

export const UploadPhotosVideos = (props: IUploadPhotosVideosProps): ReactElement => {
	const {
		fieldName,
		formik,
		handleUploadMedia,
		required = false,
		title,
		showAddButton,
		showRemoveButton = true,
	} = props;

	FileUploadDeviceCapture({ id: 'image-upload' });

	const [filesWithPreview, setFilesWithPreview] = useState<FileWithPreview[]>([]);

	useEffect((): (() => void) => {
		const files: File[] = formik.values[fieldName] || [];

		const newFiles = files.map((file) => ({
			file,
			url: URL.createObjectURL(file),
		}));

		setFilesWithPreview(newFiles);

		return () => {
			newFiles.forEach((item) => URL.revokeObjectURL(item.url));
		};
	}, [formik.values[fieldName]]);

	return (
		<div className="site-product-images-wrapper">
			<div className="site-product-header title-wrapper">
				<span className={`product-title ${required ? 'required' : ''}`}>{title}</span>

				{showAddButton && (
					<div className="image-upload-wrapper">
						<label htmlFor="image-upload">
							<span className="icon-gallery"></span>
							<span>Add</span>
						</label>
						<input
							type="file"
							name="media"
							id="image-upload"
							capture="environment"
							accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/webm,video/ogg,video/quicktime"
							multiple
							onChange={handleUploadMedia}
						/>
					</div>
				)}
			</div>

			<div className="upload-images-wrapper">
				{filesWithPreview.map(({ file, url }, idx) => {
					const isVideo = file.type.startsWith('video/');

					return (
						<div key={`${file.name}-${idx}`} className="upload-image">
							<div className="wrapper-upload-images">
								<a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
									{isVideo ? (
										isIOS ? (
											<img src={VideoIcon} alt="video" className="media-preview" />
										) : (
											<video
												src={`${url}#t=0.001`}
												className="media-preview"
												muted
												playsInline
												preload="metadata"
												onLoadedMetadata={(e) => {
													const video = e.currentTarget;
													video.currentTime = video.duration > 1 ? 1 : 0.001;
												}}
											>
												<track kind="captions" />
											</video>
										)
									) : (
										<img src={url} alt={`Uploaded ${idx + 1}`} onError={onImageError} className="media-preview" />
									)}
								</a>
							</div>

							{showRemoveButton && <RemoveImageAction fieldName={fieldName} formik={formik} imgIndex={idx} />}
						</div>
					);
				})}
			</div>
		</div>
	);
};
